import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  message,
} from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import { useSelector } from "react-redux";
import { BASE_API_URL } from "../../../utils/consts";
import { isLocationActive } from "../../../utils/helpers/locationVisibility";

interface OctopusToken {
  _id: string;
  name: string;
  token: string;
  baseUrl: string;
  locationId?: { _id: string; name: string } | null;
  createdAt: string;
}

interface Location {
  _id: string;
  name: string;
}

const OctopusSettings: React.FC = () => {
  const userData = useSelector((state: any) => state.userData.user);
  const clientId = userData?.clientId?._id || userData?.clientId;

  const [tokens, setTokens] = useState<OctopusToken[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<OctopusToken | null>(null);
  const [form] = Form.useForm();

  const loadData = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const [tokensRes, locRes] = await Promise.all([
        axios.get(`${BASE_API_URL}/${clientId}/octopus-tokens`),
        axios.get(`${BASE_API_URL}/${clientId}/locations`),
      ]);
      setTokens(tokensRes.data || []);
      setLocations((locRes.data || []).filter(isLocationActive));
    } catch (err) {
      message.error("Greška pri učitavanju podataka");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record: OctopusToken) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      baseUrl: record.baseUrl,
      token: "",
      locationId: record.locationId?._id ?? null,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      if (editing) {
        await axios.put(
          `${BASE_API_URL}/${clientId}/octopus-tokens/${editing._id}`,
          values
        );
        message.success("Token ažuriran");
      } else {
        await axios.post(`${BASE_API_URL}/${clientId}/octopus-tokens`, values);
        message.success("Token dodan");
      }
      setModalOpen(false);
      loadData();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || "Greška pri čuvanju");
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setLoading(true);
      await axios.delete(`${BASE_API_URL}/${clientId}/octopus-tokens/${id}`);
      message.success("Token obrisan");
      loadData();
    } catch {
      message.error("Greška pri brisanju");
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Naziv",
      dataIndex: "name",
      key: "name",
      render: (name: string) => <strong>{name}</strong>,
    },
    {
      title: "Lokacija",
      dataIndex: "locationId",
      key: "location",
      render: (loc: any) =>
        loc ? <Tag>{loc.name}</Tag> : <span style={{ color: "#9ca3af" }}>Sve lokacije</span>,
    },
    {
      title: "Bazni URL",
      dataIndex: "baseUrl",
      key: "baseUrl",
      render: (url: string) => (
        <code style={{ fontSize: 12, color: "#6b7280" }}>{url}</code>
      ),
    },
    {
      title: "Token",
      dataIndex: "token",
      key: "token",
      render: (tok: string) => (
        <code style={{ fontSize: 12, color: "#6b7280" }}>{tok}</code>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 80,
      render: (_: any, record: OctopusToken) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
          />
          <Popconfirm
            title="Obrisati token?"
            okText="Da"
            cancelText="Ne"
            onConfirm={() => handleDelete(record._id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Octopus API tokeni"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Dodaj token
        </Button>
      }
      loading={loading}
    >
      <Table
        dataSource={tokens}
        columns={columns}
        rowKey="_id"
        pagination={false}
        locale={{ emptyText: "Nema tokena. Dodajte prvi token." }}
      />

      <Modal
        title={editing ? "Uredi token" : "Novi token"}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText="Sačuvaj"
        cancelText="Odustani"
        confirmLoading={loading}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Naziv tokena"
            rules={[{ required: true, message: "Naziv je obavezan" }]}
          >
            <Input placeholder='npr. "Beograd - Lokacija 1"' />
          </Form.Item>

          <Form.Item name="locationId" label="Lokacija (opciono)">
            <Select
              allowClear
              placeholder="Izaberite lokaciju (prazno = sve lokacije)"
              options={locations.map((l) => ({ value: l._id, label: l.name }))}
            />
          </Form.Item>

          <Form.Item
            name="baseUrl"
            label="Bazni URL Octopus servera"
            rules={[{ required: true, message: "Bazni URL je obavezan" }]}
          >
            <Input placeholder="npr. http://89.216.105.241:8083" />
          </Form.Item>

          <Form.Item
            name="token"
            label={editing ? "Novi API token (ostavite prazno da ne mijenjate)" : "API token (Basic Auth)"}
            rules={
              editing
                ? []
                : [{ required: true, message: "Token je obavezan" }]
            }
          >
            <Input.Password
              placeholder={editing ? "Unesite novi token (opciono)" : "Unesite API token"}
              autoComplete="new-password"
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default OctopusSettings;
